import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  SelectChangeEvent,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ListData } from "../../types/List";
import { AuditLogData } from "../../types/AuditLog";
import { initialParamData, ParamData } from "../../types/Param";
import { Box } from "@mui/system";
import { RemoveRedEye } from "@mui/icons-material";
import { formatDateTime } from "../../utils/DateHelper";
import AuditDetail from "./AuditDetail";

export interface ComponentProps {
  entities: string[];
  activities: AuditLogData[];
  paramData: ParamData;
  setParamData: React.Dispatch<React.SetStateAction<ParamData>>;
  onSubmit: () => void;
}

const EntityLog: React.FC<ComponentProps> = ({
  entities,
  activities,
  paramData,
  setParamData,
  onSubmit,
}) => {
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = e.target;

    setParamData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleViewClick = async (data: AuditLogData) => {
    setExpandedRowId((prev) => (prev === data.id ? null : data.id));
  };

  const handleOnSubmit = async () => {
    if (onSubmit) {
      await onSubmit();
    }
  };

  useEffect(() => {
    if (paramData.entity && paramData.startingFrom && paramData.endingOn) {
      handleOnSubmit();
    }
  }, [paramData.entity, paramData.startingFrom, paramData.endingOn]);

  return (
    <Card sx={{ mb: 4, p: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="600" mb={2}>
          USERS' ACTIVITIES ON ENTITIES
        </Typography>

        {/* User Dropdown */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
          }}
        >
          {/* Starting From Date */}
          <TextField
            name="startingFrom"
            label="Starting From"
            type="date"
            size="small"
            value={paramData.startingFrom || ""}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0], // 🔒 max = today
            }}
            sx={{ minWidth: 170 }}
          />

          {/* Ending On Date */}
          <TextField
            name="endingOn"
            label="Ending On"
            type="date"
            size="small"
            value={paramData.endingOn || ""}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0], // 🔒 max = today
            }}
            sx={{ minWidth: 170 }}
          />
          {/* Select Entity */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Select Entity</InputLabel>
            <Select
              name="entity"
              label="Select Entity"
              onChange={handleChange}
              value={paramData.entity || ""}
            >
              <MenuItem value="">
                <em>-- Select Entity --</em>
              </MenuItem>

              {entities.map((e) => (
                <MenuItem key={e} value={e}>
                  {e}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Activities Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 450 }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>USER</TableCell>
                <TableCell>USER ACTION</TableCell>
                <TableCell>DATE</TableCell>
                <TableCell>ACTION</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {activities.map((act) => (
                <React.Fragment key={act.id}>
                  <TableRow>
                    <TableCell>{act.fullName}</TableCell>
                    <TableCell>{act.userAction}</TableCell>
                    <TableCell>{formatDateTime(act.createdAt)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          color="primary"
                          onClick={() => handleViewClick(act)}
                          size="small"
                        >
                          <RemoveRedEye />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {expandedRowId === act.id && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{ backgroundColor: "#f9f9f9" }}
                      >
                        <Box p={2}>
                          <AuditDetail details={act.auditLogDetails} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default EntityLog;
